import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Drawer, Group, ScrollArea, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { wellInputSchema, type WellInputSchema } from '../../../lib/schemas';
import { useCreateWell, useUpdateWell } from '../../../queries/wells';
import { useWellFormLayout } from '../../../queries/well-form-layout';
import { useLandLookups } from '../../../queries/land-lookups';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { useAuthStore } from '../../../auth/auth-store';
import {
  WELL_FORM_FIELDS,
  WELL_FORM_FIELD_NAMES,
  WELL_DEFAULT_LAYOUT,
  type WellFormContext,
} from '../../../lib/form-layout/well-fields';
import { FormLayoutRenderer } from '../../../lib/form-layout/FormLayoutRenderer';
import { CrossReferenceSection } from '../../../lib/CrossReferenceSection';
import { ObligationAssignmentSection } from '../../../lib/ObligationAssignmentSection';
import { ApiError } from '../../../lib/api-error';
import type { Well } from '../../../types/api';

interface Props {
  opened: boolean;
  onClose: () => void;
  well?: Well | null;
  onSaved?: (w: Well) => void;
}

const EMPTY: WellInputSchema = {
  property_number: '',
  property_name: null,
  api: null,
  alt_prop_number: null,
  prospect: null,
  field: null,
  rsrv_cat: null,
  abstract: null,
  survey: null,
  roll_up: false,
  reviewer_user_id: null,

  well_status: null,
  well_type: null,
  unit_type: null,
  fed_rights: null,
  unit_acreage: null,
  do_number: null,
  owner: null,
  operator: null,

  county: null,
  state: null,
  township: null,
  range: null,
  section: null,
  block: null,

  description: null,
  flag_for_issues: null,
  exception_remark: null,
  title_issues: null,

  oil_bpo_wi: null,
  oil_apo_wi: null,
  oil_bpo_nri: null,
  oil_apo_nri: null,
  oil_bpo_orri: null,
  oil_apo_orri: null,
  oil_bpo_ri: null,
  oil_apo_ri: null,
  gas_bpo_wi: null,
  gas_apo_wi: null,
  gas_bpo_nri: null,
  gas_apo_nri: null,
  gas_bpo_orri: null,
  gas_apo_orri: null,
  gas_bpo_ri: null,
  gas_apo_ri: null,

  minerals: false,
  royalties: false,
  participating: false,
  non_participating: false,
  term: false,
  perpetual: false,
  call_on_prod: false,
  non_consent: false,
  non_consent_penalty: null,
  payout: false,
  payout_amount: null,
  payout_date: null,

  first_prod: null,
  last_prod: null,
  oil_data_as_of: null,
  oil_cum: null,
  oil_avg_bpm_6mo: null,
  oil_avg_bopd_6mo: null,
  gas_data_as_of: null,
  gas_cum: null,
  gas_avg_mcf_6mo: null,
  gas_avg_mcfd_6mo: null,
  num_completions: null,
  total_depth: null,
  prod_zones: null,

  remarks: null,
  reviewer_notes: null,
  change_remarks: null,
};

function fromWell(w: Well): WellInputSchema {
  // node-postgres returns NUMERIC and BIGINT columns as strings (unit_acreage,
  // the 16 interest columns, oil_cum/gas_cum) to avoid precision loss —
  // convert them back to JS numbers here so zodResolver's z.number() checks
  // pass on save. INTEGER columns (oil_avg_bpm_6mo, oil_avg_bopd_6mo,
  // gas_avg_mcf_6mo, gas_avg_mcfd_6mo, num_completions, total_depth) already
  // arrive as numbers, but running them through num() too is harmless.
  // Mirrors LeaseFormDrawer.tsx's fromLease().
  const num = (v: string | number | null | undefined): number | null =>
    v === null || v === undefined ? null : typeof v === 'string' ? Number(v) : v;
  return {
    property_number: w.property_number,
    property_name: w.property_name,
    api: w.api,
    alt_prop_number: w.alt_prop_number,
    prospect: w.prospect,
    field: w.field,
    rsrv_cat: w.rsrv_cat,
    abstract: w.abstract,
    survey: w.survey,
    roll_up: w.roll_up,
    reviewer_user_id: w.reviewer_user_id,

    well_status: w.well_status,
    well_type: w.well_type,
    unit_type: w.unit_type,
    fed_rights: w.fed_rights,
    unit_acreage: num(w.unit_acreage),
    do_number: w.do_number,
    owner: w.owner,
    operator: w.operator,

    county: w.county,
    state: w.state,
    township: w.township,
    range: w.range,
    section: w.section,
    block: w.block,

    description: w.description,
    flag_for_issues: w.flag_for_issues,
    exception_remark: w.exception_remark,
    title_issues: w.title_issues,

    oil_bpo_wi: num(w.oil_bpo_wi),
    oil_apo_wi: num(w.oil_apo_wi),
    oil_bpo_nri: num(w.oil_bpo_nri),
    oil_apo_nri: num(w.oil_apo_nri),
    oil_bpo_orri: num(w.oil_bpo_orri),
    oil_apo_orri: num(w.oil_apo_orri),
    oil_bpo_ri: num(w.oil_bpo_ri),
    oil_apo_ri: num(w.oil_apo_ri),
    gas_bpo_wi: num(w.gas_bpo_wi),
    gas_apo_wi: num(w.gas_apo_wi),
    gas_bpo_nri: num(w.gas_bpo_nri),
    gas_apo_nri: num(w.gas_apo_nri),
    gas_bpo_orri: num(w.gas_bpo_orri),
    gas_apo_orri: num(w.gas_apo_orri),
    gas_bpo_ri: num(w.gas_bpo_ri),
    gas_apo_ri: num(w.gas_apo_ri),

    minerals: w.minerals,
    royalties: w.royalties,
    participating: w.participating,
    non_participating: w.non_participating,
    term: w.term,
    perpetual: w.perpetual,
    call_on_prod: w.call_on_prod,
    non_consent: w.non_consent,
    non_consent_penalty: w.non_consent_penalty,
    payout: w.payout,
    payout_amount: w.payout_amount,
    payout_date: w.payout_date,

    first_prod: w.first_prod,
    last_prod: w.last_prod,
    oil_data_as_of: w.oil_data_as_of,
    oil_cum: num(w.oil_cum),
    oil_avg_bpm_6mo: num(w.oil_avg_bpm_6mo),
    oil_avg_bopd_6mo: num(w.oil_avg_bopd_6mo),
    gas_data_as_of: w.gas_data_as_of,
    gas_cum: num(w.gas_cum),
    gas_avg_mcf_6mo: num(w.gas_avg_mcf_6mo),
    gas_avg_mcfd_6mo: num(w.gas_avg_mcfd_6mo),
    num_completions: num(w.num_completions),
    total_depth: num(w.total_depth),
    prod_zones: w.prod_zones,

    remarks: w.remarks,
    reviewer_notes: w.reviewer_notes,
    change_remarks: w.change_remarks,
  };
}

const EMPTY_LOOKUPS = {
  lease_type: [],
  lease_status: [],
  lease_ownership: [],
  fed_rights: [],
  contract_type: [],
  flag_for_issues: [],
  well_status: [],
  well_type: [],
  unit_type: [],
};

export function WellFormDrawer({ opened, onClose, well, onSaved }: Props) {
  const isEdit = !!well;
  const createM = useCreateWell();
  const updateM = useUpdateWell();
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);

  const layoutQ = useWellFormLayout(projectId);
  const layout = layoutQ.data?.layout ?? WELL_DEFAULT_LAYOUT;

  // Fetched once at the drawer's top level (never inside the renderer's
  // per-field loop, which would break the Rules of Hooks) and threaded down
  // as the FormLayoutRenderer context — the lookup-backed Selects in
  // well-fields.tsx read `context.lookups`, mirroring
  // WellFormLayoutEditor's previewContext wiring.
  const { data: lookups } = useLandLookups();

  // Project members double as the reviewer_user_id dropdown's option list
  // AND the "users lookup" for resolving the display name behind
  // updated_by_user_id — /admin/users is superadmin-gated, but any project
  // member can see the membership roster.
  const { data: members } = useProjectMembers(projectId);
  const currentUser = useAuthStore((s) => s.user);
  // Server-enforced (spec §2.4): owner/superadmin may assign any project
  // member as reviewer; moderator/editor may only assign themselves. The UI
  // mirrors this to steer users away from an obvious 403, not to replace it.
  const canAssignAnyReviewer = role === 'owner' || currentUser?.system_role === 'superadmin';
  const formContext: WellFormContext = {
    lookups: lookups ?? EMPTY_LOOKUPS,
    members: (members ?? []).map((m) => ({ value: m.user_id, label: m.username })),
    canAssignAnyReviewer,
    currentUserId: currentUser?.id ?? '',
  };

  const updatedByName = well?.updated_by_user_id
    ? (members?.find((m) => m.user_id === well.updated_by_user_id)?.username ?? well.updated_by_user_id)
    : null;

  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<WellInputSchema>({
    resolver: zodResolver(wellInputSchema),
    defaultValues: EMPTY,
  });

  const openKey = opened ? `${well?.id ?? 'new'}-${well?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) form.reset(well ? fromWell(well) : EMPTY);
  }

  async function onSubmit(values: WellInputSchema) {
    try {
      if (isEdit && well) {
        const res = await updateM.mutateAsync({ id: well.id, patch: values });
        notifications.show({
          title: 'Well saved',
          message: res.well.property_number,
          color: 'pine',
        });
        onSaved?.(res.well);
        onClose();
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({
          title: 'Well created',
          message: res.well.property_number,
          color: 'pine',
        });
        onSaved?.(res.well);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit well' : 'New well'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <FormLayoutRenderer
            layout={layout}
            control={form.control}
            register={form.register}
            registry={WELL_FORM_FIELDS}
            allNames={WELL_FORM_FIELD_NAMES}
            context={formContext}
            isEdit={isEdit}
          />

          <CrossReferenceSection entity="well" id={well?.id ?? null} />

          <ObligationAssignmentSection host="well" id={well?.id ?? null} />

          {isEdit && well && (
            <>
              <Divider />
              <Text size="sm" c="dimmed">
                {`Last updated by ${updatedByName ?? '—'} on ${new Date(
                  well.updated_at,
                ).toLocaleDateString()}`}
              </Text>
            </>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create well'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
